import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Loader2, AlertTriangle, RefreshCw, ExternalLink, FileText,
  CheckCircle2, XCircle, Users, ChevronRight, Info, Search,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { StepIcon, getStepTypeLabel } from '@/components/cadences/flowStepHelpers'
import { useCadenceFlowMetrics, type FlowStep, type PromptSummary } from '@/hooks/useCadenceFlowMetrics'
import { useCadence } from '@/contexts/CadenceContext'
import { stepGeneratesMessage, stepNeedsRubric } from '@/lib/cadence-step-predicates'
import { PromptDrawer, type PromptDrawerStep } from './PromptDrawer'

// Per-step shape returned by cadence-flow-metrics — the SHARED FlowStep type (it
// carries `prompt` / `research_prompt` / `completeness`). No local re-declaration.
//
// Message-gating (prompt/angle) uses stepGeneratesMessage; rubric-gating uses
// stepNeedsRubric (both from @/lib/cadence-step-predicates). A linkedin_comment
// step IS a message step (angle card) but does NOT need a rubric (Carlos scores
// comments with structural rules, not a numeric rubric) — so its rubric chip is
// replaced by a gray note instead of an irresoluble amber "Sin rúbrica".

type ChipTab = 'policy' | 'angles'

function CompletenessChip({
  ok,
  okLabel,
  missLabel,
  tab,
  onGoTab,
}: {
  ok: boolean
  okLabel: string
  missLabel: string
  tab?: ChipTab
  onGoTab?: (tab: ChipTab) => void
}) {
  if (ok) {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[10px] font-normal"
      >
        <CheckCircle2 className="h-3 w-3" />
        {okLabel}
      </Badge>
    )
  }
  const chip = (
    <Badge
      variant="outline"
      className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[10px] font-normal"
    >
      <XCircle className="h-3 w-3" />
      {missLabel}
    </Badge>
  )
  if (tab && onGoTab) {
    return (
      <button type="button" onClick={() => onGoTab(tab)} className="focus:outline-none">
        {chip}
      </button>
    )
  }
  return chip
}

// A single clickable prompt row (message or research). When `label` is set it
// prints a small caption above the name so the reader knows which prompt this is.
function PromptLine({
  prompt,
  label,
  icon,
  onOpen,
}: {
  prompt: PromptSummary
  label?: string
  icon?: ReactNode
  onOpen: () => void
}) {
  return (
    <div className="space-y-1">
      {label && (
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-muted/60"
      >
        {icon ?? <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {prompt.name ?? 'Prompt asignado'}
        </span>
        {prompt.language && (
          <Badge variant="outline" className="text-[10px] uppercase">
            {prompt.language}
          </Badge>
        )}
        {prompt.body_length > 0 && (
          <span className="hidden shrink-0 text-[11px] text-muted-foreground sm:inline">
            {prompt.body_length.toLocaleString('es-ES')} chars
          </span>
        )}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// StepsSection — the "Steps" tab. Reads the enriched cadence-flow-metrics for the
// cadence in context and shows, per step (day_offset/order order): icon + label,
// completeness chips (prompt / rubric / angle) with links to the tab that fixes a
// gap, and — when the step has a prompt — its name + a "Ver prompt" button that
// opens the read-only PromptDrawer. The builder stays as a secondary action.
// ────────────────────────────────────────────────────────────────────────────
export function StepsSection({
  cadenceId,
  onGoTab,
}: {
  cadenceId: string
  onGoTab?: (tab: ChipTab) => void
}) {
  const navigate = useNavigate()
  const { data, isLoading, error, refetch, isFetching } = useCadenceFlowMetrics(cadenceId)
  const { cadences } = useCadence()
  const [drawerStep, setDrawerStep] = useState<PromptDrawerStep | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // flow-metrics StepOut does not carry config_json, so read config_json.send_note
  // from the cadence in context (CadenceStep) keyed by step id, to mirror the
  // runtime predicate exactly rather than inferring from prompt presence.
  const configByStepId = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>()
    const cadence = cadences.find((c) => c.id === cadenceId)
    for (const s of cadence?.steps ?? []) map.set(s.id, s.config_json ?? {})
    return map
  }, [cadences, cadenceId])

  const steps = useMemo<FlowStep[]>(() => {
    const raw = data?.steps ?? []
    return [...raw].sort((a, b) => {
      if (a.day_offset !== b.day_offset) return a.day_offset - b.day_offset
      return a.order_in_day - b.order_in_day
    })
  }, [data])

  // [D] How many steps of THIS cadence reference each ai_prompt_id. In prod a
  // single prompt can be shared by 2+ steps; editing it changes all of them, so
  // the drawer must warn. Counted from the cadence context config_json (the same
  // source used for the send_note predicate above).
  const stepCountByPromptId = useMemo(() => {
    const counts = new Map<string, number>()
    for (const cfg of configByStepId.values()) {
      const pid = cfg?.ai_prompt_id
      if (typeof pid === 'string' && pid.length > 0) {
        counts.set(pid, (counts.get(pid) ?? 0) + 1)
      }
    }
    return counts
  }, [configByStepId])

  // Open the drawer for a specific prompt of a step (message OR research). The
  // drawer is prompt-agnostic; we hand it whichever summary the user clicked,
  // plus how many steps of the cadence share that prompt (so it can warn on a
  // shared edit). Research prompts are not counted here — the map keys on
  // ai_prompt_id only — so a research summary reports sharedByStepCount=0.
  const openPrompt = (s: FlowStep, prompt: PromptSummary) => {
    setDrawerStep({
      stepId: s.step_id,
      dayOffset: s.day_offset,
      stepType: s.step_type,
      stepLabel: s.step_label,
      promptId: prompt.id ?? null,
      promptName: prompt.name ?? null,
      promptType: prompt.prompt_type ?? null,
      tone: prompt.tone ?? null,
      language: prompt.language ?? null,
      objective: prompt.objective ?? null,
      bodyLength: prompt.body_length ?? 0,
      hasVariables: prompt.has_variables ?? false,
      sharedByStepCount: prompt.id ? (stepCountByPromptId.get(prompt.id) ?? 0) : 0,
      // [D] Per-step injection reality so the drawer's "Qué más se inyecta"
      // list reflects THIS day, not four static assumptions. A day with no
      // touch_angles row → tiene_angle=false → no angle-lock injected at
      // runtime. Research is injected as {{research}} only when the step
      // actually resolves a research prompt (a dangling id yields
      // has_research_prompt=true but research_prompt=null → not injected).
      hasAngle: s.completeness?.tiene_angle ?? false,
      hasResearchPrompt: (s.config?.has_research_prompt ?? false) && !!s.research_prompt,
      // [D] Effective step-specific Carlos rubric (system_prompt_addendum),
      // surfaced read-only in the drawer. Exposed by the edge fn in carril 4;
      // ?? null tolerates old payloads where the field is absent.
      carlosAddendum: s.carlos?.system_prompt_addendum ?? null,
      // [D] The EFFECTIVE Carlos values (the column that MANDA). Passed so the
      // drawer can show them next to the addendum prose — if the prose drifted
      // ("Threshold 8.0") the reader still sees the real effective threshold.
      carlosThreshold: s.carlos?.threshold ?? null,
      carlosMinAcceptable: s.carlos?.min_acceptable ?? null,
      carlosMaxAttempts: s.carlos?.max_attempts ?? null,
    })
    setDrawerOpen(true)
  }

  return (
    <div className="space-y-6">
      {/* Business rationale + section actions */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 max-w-2xl">
          <Users className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-sm text-muted-foreground">
            Cada paso es un <strong>touch</strong> hacia el lead enterprise: define qué se envía y
            cuándo. Aquí ves, para cada uno, si tiene lo que necesita para redactarse solo — su
            <strong> prompt</strong> (cómo se escribe), su <strong>rúbrica</strong> (cómo lo juzga
            Carlos) y su <strong>ángulo</strong> (qué propuesta de valor lleva). Abre cualquier
            prompt para leer exactamente qué hay detrás del mensaje.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`mr-1 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/cadences/${cadenceId}`)}
          >
            <ExternalLink className="mr-1.5 h-4 w-4" />
            Editar pasos en el builder
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card><CardContent className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando los pasos de la cadencia…
        </CardContent></Card>
      ) : error ? (
        <Card className="border-red-200 dark:border-red-900"><CardContent className="py-8 text-center text-sm">
          <p className="flex items-center justify-center gap-1.5 text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" /> No se pudieron leer los pasos de la cadencia.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Reintentar
          </Button>
        </CardContent></Card>
      ) : steps.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center">
          <FileText className="mx-auto h-9 w-9 text-muted-foreground" />
          <h3 className="mt-3 text-base font-semibold">Esta cadencia aún no tiene pasos</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Agrega los pasos en el builder visual; luego vuelve aquí para ver el prompt y la
            completitud de cada touch.
          </p>
          <Button className="mt-4" onClick={() => navigate(`/cadences/${cadenceId}`)}>
            <ExternalLink className="mr-1.5 h-4 w-4" />
            Ir al builder
          </Button>
        </CardContent></Card>
      ) : (
        <TooltipProvider delayDuration={200}>
          <div className="space-y-3">
            {steps.map((s) => {
              const predicateInput = {
                step_type: s.step_type,
                config_json: configByStepId.get(s.step_id),
              }
              const isMessage = stepGeneratesMessage(predicateInput)
              // A linkedin_comment IS a message step but is validated by
              // structural rules, not a numeric rubric — so it needs no rubric
              // chip. `needsRubric` gates the rubric chip + the effective-rubric
              // block; message steps that don't need one show a gray note.
              const needsRubric = stepNeedsRubric(predicateInput)
              // The effective-rubric block renders only when a threshold resolved
              // (no fabricated values). Reused by the `task` label so its tooltip
              // never promises a block that isn't shown.
              const hasEffectiveRubric = needsRubric && s.carlos?.threshold !== null
              const hasPrompt = s.completeness?.tiene_prompt ?? !!s.prompt
              // A step can also inject a research prompt as {{research}} into the
              // message. Only surface it when we resolved its summary (a dangling
              // id yields has_research_prompt=true but research_prompt=null).
              const hasResearchPrompt = (s.config?.has_research_prompt ?? false) && !!s.research_prompt
              const carlosUnscored = isMessage && (s.carlos?.samples ?? 0) === 0
              return (
                <Card key={s.step_id}>
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <StepIcon stepType={s.step_type} className="size-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="secondary" className="font-mono text-xs">
                              Day {s.day_offset}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {getStepTypeLabel(s.step_type)}
                            </span>
                          </div>
                          <CardTitle className="mt-1 truncate text-base">{s.step_label}</CardTitle>
                        </div>
                      </div>

                      {hasPrompt && s.prompt && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          onClick={() => openPrompt(s, s.prompt!)}
                        >
                          <FileText className="mr-1.5 h-4 w-4" />
                          Ver prompt
                        </Button>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3 pt-0">
                    {/* Completeness chips */}
                    {isMessage ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <CompletenessChip
                          ok={hasPrompt}
                          okLabel="Prompt"
                          missLabel="Sin prompt"
                        />
                        {/* Rubric chip ONLY for steps Carlos scores with a rubric.
                            A linkedin_comment is a message step but needs no
                            rubric — show a gray "structural rules" note instead of
                            an irresoluble amber "Sin rúbrica". */}
                        {needsRubric ? (
                          <CompletenessChip
                            ok={s.completeness?.tiene_rubrica ?? false}
                            okLabel="Rúbrica"
                            missLabel="Sin rúbrica"
                            tab="policy"
                            onGoTab={onGoTab}
                          />
                        ) : (
                          <Badge
                            variant="outline"
                            className="gap-1 text-[10px] font-normal text-muted-foreground"
                          >
                            Validado por reglas estructurales (sin rúbrica numérica)
                          </Badge>
                        )}
                        <CompletenessChip
                          ok={s.completeness?.tiene_angle ?? false}
                          okLabel="Ángulo"
                          missLabel="Sin ángulo"
                          tab="angles"
                          onGoTab={onGoTab}
                        />
                      </div>
                    ) : needsRubric ? (
                      // [D] A `task` step (e.g. the Day-9 BC-attachment) does NOT
                      // generate an AI message (stepGeneratesMessage=false) but IS
                      // scored by Carlos with a rubric (stepNeedsRubric=true). The
                      // old non-message branch labelled it "Sin generación IA / no
                      // necesita rúbrica", contradicting the effective-rubric block
                      // shown below. Give it a coherent label instead: no AI copy,
                      // but yes scored by Carlos.
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="gap-1 border-primary/30 bg-primary/[0.06] text-[10px] font-normal text-foreground cursor-help">
                            <CheckCircle2 className="h-3 w-3 text-primary" />
                            Tarea (adjunto BC) — sí puntuada por Carlos
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs">
                          {hasEffectiveRubric
                            ? 'Este paso no redacta un mensaje con IA, pero Carlos sí puntúa su copy con la rúbrica efectiva (abajo). No lleva ángulo.'
                            : 'Este paso no redacta un mensaje con IA, pero Carlos sí lo puntúa con una rúbrica. Aún no hay una rúbrica efectiva resuelta para este paso — configúrala en Política. No lleva ángulo.'}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground cursor-help">
                            Sin generación IA
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs">
                          {s.step_type === 'linkedin_connect'
                            ? 'Invitación en frío sin nota — sale sin mensaje generado por IA, así que no necesita prompt, rúbrica ni ángulo.'
                            : (s.step_type === 'whatsapp' || s.step_type === 'cold_call')
                              ? 'Este paso es un canal manual (lo redacta o lo hace una persona), no un mensaje generado por IA — por eso no aparece en Ángulos ni en Política y no necesita prompt, rúbrica ni ángulo.'
                              : 'Este paso no redacta un mensaje con IA (por ejemplo, una acción de LinkedIn), así que no aparece en Ángulos ni en Política y no necesita prompt, rúbrica ni ángulo.'}
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Effective Carlos rubric (read-only), §2.2 layer-2: the
                        rubric that governs THIS step must be visible in the step
                        view, not only in the Política tab. Rendered only for steps
                        Carlos scores with a rubric AND that resolve to an effective
                        threshold (cadence-scoped or inherited). If threshold is
                        null (no effective rubric) we omit it — no fabricated
                        values. Editing routes to the Política tab. */}
                    {hasEffectiveRubric && (
                      <div className="flex items-start justify-between gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
                        <span className="text-muted-foreground">
                          <span className="font-medium text-foreground">Rúbrica de Carlos</span>
                          {' — umbral '}
                          <span className="font-mono">{s.carlos.threshold}</span>
                          {' · piso '}
                          <span className="font-mono">{s.carlos.min_acceptable ?? '—'}</span>
                          {' · '}
                          <span className="font-mono">{s.carlos.max_attempts ?? '—'}</span>
                          {' reintentos'}
                        </span>
                        {onGoTab && (
                          <button
                            type="button"
                            onClick={() => onGoTab('policy')}
                            className="shrink-0 font-medium text-primary hover:underline focus:outline-none"
                          >
                            Editar en Política
                          </button>
                        )}
                      </div>
                    )}

                    {/* Carlos honest note — no fabricated average when unscored */}
                    {carlosUnscored && (
                      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          Este paso pasa los gates deterministas; Carlos solo puntúa en burn-in o
                          cuando un gate falla — hoy no ha puntuado mensajes de este paso en esta
                          cadencia.
                        </span>
                      </div>
                    )}

                    {/* Prompt lines — message prompt and, when present, the research
                        prompt injected as {{research}} into that message. Each opens
                        the same read-only drawer for its own prompt. */}
                    {hasPrompt && s.prompt ? (
                      <PromptLine
                        prompt={s.prompt}
                        label={hasResearchPrompt ? 'Prompt de mensaje' : undefined}
                        onOpen={() => openPrompt(s, s.prompt!)}
                      />
                    ) : isMessage ? (
                      <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        Este paso redacta un mensaje pero no tiene prompt asignado — usará el fallback
                        del motor. Asígnalo en el builder.
                      </div>
                    ) : null}

                    {hasResearchPrompt && s.research_prompt && (
                      <PromptLine
                        prompt={s.research_prompt}
                        label="Prompt de research · {{research}}"
                        icon={<Search className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        onOpen={() => openPrompt(s, s.research_prompt!)}
                      />
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </TooltipProvider>
      )}

      {/* Read-only prompt drawer */}
      <PromptDrawer step={drawerStep} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  )
}
